import React, { useMemo, useState, useEffect } from 'react';
import { useCreateDriverMutation, useUpdateDriverMutation, Driver, useGetVehiclesQuery, useGetCarriersQuery } from '../../store/api/referencesApi';
import { useGetUsersQuery } from '../../store/api/usersApi';
import { formatResponsibleUserLabel } from '../../utils/userDisplayName';
import VehicleSelectInline from './VehicleSelectInline';
import EntityDocumentsBlock from '../documents/EntityDocumentsBlock';

interface CreateDriverModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDriverCreated?: (driver: Driver) => void;
  driver?: Driver | null; // Для режима редактирования
}

const fieldLabelClass =
  'block text-sm font-medium text-gray-700 mb-2';

const CreateDriverModal: React.FC<CreateDriverModalProps> = ({
  isOpen,
  onClose,
  onDriverCreated,
  driver = null
}) => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    passportData: '',
    driverLicense: '',
    isCompanyDriver: false,
    defaultVehicleId: '',
    defaultTrailerVehicleId: '',
    assignedLogistId: '',
    carrierId: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [createDriver, { isLoading: isCreating }] = useCreateDriverMutation();
  const [updateDriver, { isLoading: isUpdating }] = useUpdateDriverMutation();
  const { data: vehicles, refetch: refetchVehicles } = useGetVehiclesQuery();
  const { data: users = [] } = useGetUsersQuery();
  const { data: carriers = [] } = useGetCarriersQuery();

  const trucks = useMemo(
    () => (vehicles || []).filter((v) => v.vehicleCategory !== 'TRAILER'),
    [vehicles]
  );
  const trailers = useMemo(
    () => (vehicles || []).filter((v) => v.vehicleCategory === 'TRAILER'),
    [vehicles]
  );

  const isLoading = isCreating || isUpdating;
  const isEditMode = !!driver;

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'ФИО обязательно для заполнения';
    }
    if (!formData.defaultVehicleId) {
      newErrors.defaultVehicleId = 'Выберите тягач по умолчанию';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const truckId = Number(formData.defaultVehicleId);
    const trailerPayload =
      formData.defaultTrailerVehicleId && String(formData.defaultTrailerVehicleId).trim() !== ''
        ? Number(formData.defaultTrailerVehicleId)
        : null;

    try {
      if (isEditMode && driver) {
        const updatedDriver = await updateDriver({
          id: driver.id,
          data: {
            fullName: formData.fullName.trim(),
            phone: formData.phone.trim() || undefined,
            passportData: formData.passportData.trim() || undefined,
            driverLicense: formData.driverLicense.trim() || undefined,
            isCompanyDriver: formData.isCompanyDriver,
            defaultVehicleId: truckId,
            defaultTrailerVehicleId: trailerPayload,
            assignedLogistId: formData.isCompanyDriver && formData.assignedLogistId
              ? Number(formData.assignedLogistId)
              : null,
            carrierId: !formData.isCompanyDriver && formData.carrierId
              ? Number(formData.carrierId)
              : null
          }
        }).unwrap();
        onDriverCreated?.(updatedDriver as unknown as Driver);
      } else {
        const newDriver = await createDriver({
          fullName: formData.fullName.trim(),
          phone: formData.phone.trim() || undefined,
          passportData: formData.passportData.trim() || undefined,
          driverLicense: formData.driverLicense.trim() || undefined,
          isCompanyDriver: formData.isCompanyDriver,
          defaultVehicleId: truckId,
          defaultTrailerVehicleId: trailerPayload,
          assignedLogistId: formData.isCompanyDriver && formData.assignedLogistId
            ? Number(formData.assignedLogistId)
            : null,
          carrierId: !formData.isCompanyDriver && formData.carrierId
            ? Number(formData.carrierId)
            : null
        }).unwrap();
        onDriverCreated?.(newDriver as unknown as Driver);
      }
      onClose();
      setFormData({
        fullName: '',
        phone: '',
        passportData: '',
        driverLicense: '',
        isCompanyDriver: false,
        defaultVehicleId: '',
        defaultTrailerVehicleId: '',
        assignedLogistId: '',
        carrierId: ''
      });
      setErrors({});
    } catch (error: any) {
      setErrors({ general: error.data?.message || 'Ошибка при создании/обновлении водителя' });
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  // Заполняем форму данными для редактирования
  useEffect(() => {
    if (isOpen && driver) {
      setFormData({
        fullName: driver.fullName,
        phone: driver.phone || '',
        passportData: driver.passportData || '',
        driverLicense: driver.driverLicense || '',
        isCompanyDriver: !!(driver as Driver).isCompanyDriver,
        defaultVehicleId: driver.defaultVehicleId != null ? String(driver.defaultVehicleId) : '',
        defaultTrailerVehicleId:
          driver.defaultTrailerVehicleId != null ? String(driver.defaultTrailerVehicleId) : '',
        assignedLogistId: (driver.assignedLogistId ?? '').toString(),
        carrierId: (driver.carrierId ?? '').toString()
      });
    } else if (isOpen && !driver) {
      setFormData({
        fullName: '',
        phone: '',
        passportData: '',
        driverLicense: '',
        isCompanyDriver: false,
        defaultVehicleId: '',
        defaultTrailerVehicleId: '',
        assignedLogistId: '',
        carrierId: ''
      });
    }
    setErrors({});
  }, [isOpen, driver]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          {isEditMode ? 'Редактировать водителя' : 'Создать водителя'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* ФИО */}
          <div>
            <label className={fieldLabelClass}>ФИО *</label>
            <input
              type="text"
              value={formData.fullName}
              onChange={(e) => handleChange('fullName', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.fullName ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Введите ФИО водителя"
            />
            {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
          </div>

          {/* Телефон */}
          <div>
            <label className={fieldLabelClass}>Телефон</label>
            <input
              type="text"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Введите телефон"
            />
          </div>

          {/* Паспортные данные */}
          <div>
            <label className={fieldLabelClass}>Паспортные данные</label>
            <input
              type="text"
              value={formData.passportData}
              onChange={(e) => handleChange('passportData', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Серия и номер паспорта"
            />
          </div>

          {/* Водительские права */}
          <div>
            <label className={fieldLabelClass}>Водительские права</label>
            <input
              type="text"
              value={formData.driverLicense}
              onChange={(e) => handleChange('driverLicense', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Серия и номер прав"
            />
          </div>

          {/* Признак водитель компании */}
          <div>
            <label className={fieldLabelClass}>Водитель компании</label>
            <input
              type="checkbox"
              checked={formData.isCompanyDriver}
              onChange={(e) => {
                const checked = e.target.checked;
                setFormData((prev) => {
                  const next = { ...prev, isCompanyDriver: checked };
                  if (checked) next.carrierId = '';
                  else next.assignedLogistId = '';
                  return next;
                });
              }}
              className="mr-2"
            />
          </div>

          {/* Ответственный логист (только для водителей компании) */}
          {formData.isCompanyDriver && (
            <div>
              <label className={fieldLabelClass}>Ответственный логист</label>
              <select
                value={formData.assignedLogistId}
                onChange={(e) => handleChange('assignedLogistId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Не выбран</option>
                {users
                  .filter((u: { role?: unknown }) => {
                    const roleName = typeof u.role === 'string' ? u.role : (u.role as { name?: string })?.name;
                    return String(roleName || '').toLowerCase() === 'logist';
                  })
                  .map((u: { id: number; username?: string; email?: string; firstName?: string; lastName?: string; role?: { name?: string } }) => (
                    <option key={u.id} value={u.id}>
                      {formatResponsibleUserLabel(u)}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {/* Перевозчик (только для не-компанийных водителей) */}
          {!formData.isCompanyDriver && (
            <div>
              <label className={fieldLabelClass}>Перевозчик</label>
              <select
                value={formData.carrierId}
                onChange={(e) => handleChange('carrierId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Выберите перевозчика</option>
                {(carriers || []).map((carrier: { id: number; name: string }) => (
                  <option key={carrier.id} value={carrier.id}>
                    {carrier.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Тягач по умолчанию */}
          <VehicleSelectInline
            label="Тягач по умолчанию *"
            hint="Подставляется в маршруте"
            vehicles={trucks}
            placeholder="Выберите тягач"
            allowClear={false}
            modalCategory="TRUCK"
            addButtonTooltip="Добавить тягач"
            addButtonAriaLabel="Добавить тягач"
            onRefetchVehicles={() => refetchVehicles()}
            value={formData.defaultVehicleId ? Number(formData.defaultVehicleId) : undefined}
            onChange={(id) => handleChange('defaultVehicleId', id != null ? String(id) : '')}
            error={!!errors.defaultVehicleId}
            errorMessage={errors.defaultVehicleId}
          />

          {/* Прицеп по умолчанию */}
          <VehicleSelectInline
            label="Прицеп по умолчанию"
            vehicles={trailers}
            placeholder="Выберите прицеп"
            allowClear
            modalCategory="TRAILER"
            addButtonTooltip="Добавить прицеп"
            addButtonAriaLabel="Добавить прицеп"
            onRefetchVehicles={() => refetchVehicles()}
            value={
              formData.defaultTrailerVehicleId && String(formData.defaultTrailerVehicleId).trim() !== ''
                ? Number(formData.defaultTrailerVehicleId)
                : undefined
            }
            onChange={(id) => handleChange('defaultTrailerVehicleId', id != null ? String(id) : '')}
          />

          {isEditMode && driver ? (
            <div className="border border-gray-200 rounded-md p-3">
              <div className="text-sm font-medium text-gray-700 mb-2">Документы</div>
              <EntityDocumentsBlock
                entityType="driver"
                entityId={driver.id}
                canModify
                documentDetails={{
                  PASSPORT: { number: formData.passportData },
                  DRIVER_LICENSE: { number: formData.driverLicense }
                }}
              />
            </div>
          ) : (
            <div className="border border-gray-200 rounded-md p-3 text-sm text-gray-500">
              Документы можно загрузить после создания водителя.
            </div>
          )}

          {errors.general && <div className="text-red-500 text-sm">{errors.general}</div>}

          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200">
              Отмена
            </button>
            <button type="submit" disabled={isLoading} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
              {isLoading ? (isEditMode ? 'Обновление…' : 'Создание…') : isEditMode ? 'Обновить' : 'Создать'}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

export default CreateDriverModal;
